import {
  Flex,
  FlexItem,
  FormSelect,
  FormSelectOption,
  PageSection,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import {
  useInstrumentsForPatient,
  usePatientInstrumentResponses,
} from '@app/domain/instrument/instrument.hooks'
import type { IInstrumentMetadata } from '@app/domain/instrument/instrument.interface'
import type { IPatientId } from '@app/domain/patient/patient.interface'

import { ResponseHistoryTable } from './response-history-list'

const Wrapper = styled.div`
  .version {
    padding-right: 2em;
  }
`

interface InstrumentSelectProps {
  instrumentMetadata: IInstrumentMetadata[]
  onInstrumentSelected: (instrumentId: string, version: number) => void
}

export const InstrumentSelect = ({
  instrumentMetadata,
  onInstrumentSelected,
}: InstrumentSelectProps) => {
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string>(null)
  const [selectedVersion, setSelectedVersion] = useState<number>(null)
  const [allVersions, setAllVersions] = useState<number[]>([])
  const [completedVersions, setCompletedVersions] = useState<number[]>([])

  const handleInstrumentSelected = useCallback(
    (newInstrumentId: string) => {
      const selectedInstrument = instrumentMetadata.find(
        (x) => x.instrumentId === newInstrumentId,
      )
      if (selectedInstrument) {
        const selectedInstrumentId = selectedInstrument.instrumentId
        const allVersions = selectedInstrument.allVersions.sort((a, b) => b - a)
        const completedVersions = selectedInstrument.completedVersions.sort(
          (a, b) => b - a,
        )
        const selectedVersion = completedVersions[0]

        setCompletedVersions(completedVersions)
        setAllVersions(allVersions)

        setSelectedInstrumentId(selectedInstrumentId)
        setSelectedVersion(selectedVersion)
        onInstrumentSelected(selectedInstrumentId, selectedVersion)
      }
    },
    [instrumentMetadata, onInstrumentSelected],
  )

  const handleInstrumentVersionSelected = useCallback(
    (newInstrumentVersion: string) => {
      const version = parseInt(newInstrumentVersion)
      setSelectedVersion(version)
      onInstrumentSelected(selectedInstrumentId, version)
    },
    [onInstrumentSelected, selectedInstrumentId],
  )

  useEffect(() => {
    if (instrumentMetadata && instrumentMetadata.length > 0) {
      const selectedInstrument = instrumentMetadata[0]
      handleInstrumentSelected(selectedInstrument.instrumentId)
    }
  }, [handleInstrumentSelected, instrumentMetadata])

  const hasInstrumentMetadata = (instrumentMetadata?.length ?? 0) > 0

  return (
    <Wrapper>
      {hasInstrumentMetadata ? (
        <Flex
          spaceItems={{ default: 'spaceItemsLg' }}
          direction={{ default: 'row' }}
        >
          <FlexItem>
            <FormSelect
              value={selectedInstrumentId}
              onChange={handleInstrumentSelected}
              aria-label="FormSelect Input"
            >
              {instrumentMetadata.map((option, index) => (
                <FormSelectOption
                  key={index}
                  value={option.instrumentId}
                  label={displayLabelIfExists({
                    id: option.instrumentId,
                    label: option.label,
                  })}
                />
              ))}
            </FormSelect>
          </FlexItem>
          <FlexItem>
            <FormSelect
              value={selectedVersion?.toString() ?? ''}
              onChange={handleInstrumentVersionSelected}
              aria-label="FormSelect Input"
              className="version"
            >
              {allVersions.map((version) => (
                <FormSelectOption
                  key={version.toString()}
                  value={version.toString()}
                  label={`Version: ${version + 1}`}
                  isDisabled={!completedVersions.includes(version)}
                />
              ))}
            </FormSelect>
          </FlexItem>
        </Flex>
      ) : null}
    </Wrapper>
  )
}

interface Props {
  patientId: IPatientId
}

export const ResponseHistory = ({ patientId }: Props) => {
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string>(null)
  const [selectedInstrumentVersion, setSelectedInstrumentVersion] = useState<
    number
  >(null)

  const { entries, editResponses } = usePatientInstrumentResponses(
    selectedInstrumentId,
    selectedInstrumentVersion,
    patientId,
  )

  const { instrumentMetadata } = useInstrumentsForPatient(patientId)

  const handleInstrumentSelected = useCallback(
    (instrumentId: string, instrumentVersion: number) => {
      setSelectedInstrumentId(instrumentId)
      setSelectedInstrumentVersion(instrumentVersion)
    },
    [],
  )

  const handleEditResponses = useCallback(
    (entryId: string, responses: Record<string, string>) => {
      editResponses(entryId, responses)
    },
    [editResponses],
  )

  if (instrumentMetadata?.length > 0) {
    return (
      <>
        <InstrumentSelect
          instrumentMetadata={instrumentMetadata}
          onInstrumentSelected={handleInstrumentSelected}
        />
        {selectedInstrumentId ? (
          <ResponseHistoryTable
            entries={entries ?? []}
            key={`${selectedInstrumentId}_${selectedInstrumentVersion}`}
            instrumentId={selectedInstrumentId}
            instrumentVersion={selectedInstrumentVersion}
            isEditable={true}
            onEditResponses={handleEditResponses}
          />
        ) : (
          <PageSection key={0}>No Instrument Selected</PageSection>
        )}
      </>
    )
  } else {
    return <PageSection key={0}>Patient has no response history.</PageSection>
  }
}
